import { imageFieldsFrag } from './imageFieldsFrag'
import { type SanitizedResult } from '@amzn/bf-gql'
import { gql } from './gql'
import { playableEntityFrag } from './playableEntityFrag'

// The display fields a browse CARD needs — everything except the per-episode
// stream resolution (`playbackInformation`) and per-user `eligibility`. The
// fan-out browse surfaces (category / provider / see-more shelves) render
// episodes as cards and play them lazily through the cloud queue, so they take
// this lean shape: selecting `playbackInformation` there forces the gateway to
// resolve real HLS stream/license URLs for every episode across every row,
// which 500s / times out. Mirrors GH's category-card fragment, which omits the
// stream and resolves it only on the detail/play path. Full = card + eligibility
// + playbackInformation (below); mirrors the trackCommonFields / trackFields
// split in {@link trackFieldsFrag}.
// language=GraphQL
export const podcastEpisodeCardFieldsFrag = gql(
    `
        fragment podcastEpisodeCardFields on PodcastEpisode @_unmask {
            ...playableEntity
            id
            url
            title
            author
            description
            duration
            releaseDate
            contentBenefitTypes
            images {
                ...imageFields
            }
            podcast {
                id
                title
                author
                followsContext @include(if: $isAuthenticated) {
                    isFollowed
                }
            }
            libraryContext @include(if: $isAuthenticated) {
                isInLibrary
            }
        }
    `,
    [imageFieldsFrag, playableEntityFrag]
)

export type PodcastEpisodeCardFields = SanitizedResult<typeof podcastEpisodeCardFieldsFrag>

// The full episode fragment — card fields plus the heavy per-episode stream
// resolution + per-user eligibility. Used by the play / queue / detail surfaces
// that actually load or gate an episode (queue view, now-playing, episode detail
// page, related-episodes shelf). Do NOT use this on fan-out browse cards.
// language=GraphQL
export const podcastEpisodeFieldsFrag = gql(
    `
        fragment podcastEpisodeFields on PodcastEpisode @_unmask {
            ...podcastEpisodeCardFields
            eligibility {
                playbackSubscriptionTiers
                # Firefly denies these per-user eligibility checks to anonymous
                # callers; gate them on auth so an anon page issues a public-only
                # query rather than a partial-error response.
                playback @include(if: $isAuthenticated) {
                    canPlayOnDemand
                    shuffleType
                }
                isDownloadable @include(if: $isAuthenticated)
            }
            playbackInformation(videoStreamingFormat: HLS_VND) {
                audioUrl
                videoUrl
                videoStreamingFormat
                # progressMilliseconds/completedState are per-user resume state
                # Firefly owns and rejects for anonymous callers; gate them on
                # auth so audioUrl (the public stream) stays fetchable anon
                # without the query partial-erroring.
                progressMilliseconds @include(if: $isAuthenticated)
                completedState @include(if: $isAuthenticated)
            }
        }
    `,
    [podcastEpisodeCardFieldsFrag]
)

export type PodcastEpisodeFields = SanitizedResult<typeof podcastEpisodeFieldsFrag>
